import React from 'react'

import { InvoiceStatus } from '../../types'
import InvoiceMetaCardLoading from './invoiceMetaCardLoading'
import './invoiceMetaCard.scss'

type Props = {
  [x: string]: any
  name?: string
  defaultValue?: string | string[] | undefined
  showAll?: boolean
  value?: InvoiceStatus
  excludeDraft?: boolean
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
  disabled?: boolean
  loading?: boolean
}

const statusCardClass = (status?: InvoiceStatus): string => {
  switch (status) {
    case InvoiceStatus.PAID:
      return 'invoice-meta-card--status-paid'
    case InvoiceStatus.OVERDUE:
      return 'invoice-meta-card--status-overdue'
    case InvoiceStatus.DRAFT:
      return 'invoice-meta-card--status-draft'
    case InvoiceStatus.OPEN:
    default:
      return 'invoice-meta-card--status-open'
  }
}

const ALL_STATUS_OPTIONS = [
  { value: InvoiceStatus.DRAFT, label: 'Draft' },
  { value: InvoiceStatus.OPEN, label: 'Open' },
  { value: InvoiceStatus.OVERDUE, label: 'Overdue' },
  { value: InvoiceStatus.PAID, label: 'Paid' },
]

const makeStatusChangeEvent = (
  name: string,
  value: InvoiceStatus,
): React.ChangeEvent<HTMLSelectElement> => ({
  target: { name, value },
} as React.ChangeEvent<HTMLSelectElement>)

const StatusSelector: React.FC<Props> = ({
  name = 'status',
  value,
  onChange,
  excludeDraft,
  disabled,
  loading = false,
}) => {
  const options = excludeDraft
    ? ALL_STATUS_OPTIONS.filter((opt) => opt.value !== InvoiceStatus.DRAFT)
    : ALL_STATUS_OPTIONS

  const status = value as InvoiceStatus | undefined

  if (loading) {
    return (
      <InvoiceMetaCardLoading
        message='Updating status'
        className={statusCardClass(status)}
      />
    )
  }

  return (
    <div className={`invoice-meta-card invoice-status-stepper ${statusCardClass(status)}`}>
      <div className='invoice-meta-card__copy mb-2'>
        <span className='invoice-meta-card__title'>Status</span>
        <small className='help-tip-text'> Update by tapping on the status name</small>
      </div>
      <div className='invoice-status-stepper__track' role='group' aria-label='Invoice lifecycle'>
        {options.map((opt, index) => {
          const isActive = status === opt.value
          return (
            <React.Fragment key={opt.value}>
              {index > 0 && <div className='invoice-status-stepper__connector' aria-hidden='true' />}
              <button
                type='button'
                className={[
                  'invoice-status-stepper__node',
                  `invoice-status-stepper__node--${opt.value}`,
                  isActive ? 'invoice-status-stepper__node--active' : '',
                ].filter(Boolean).join(' ')}
                aria-pressed={isActive}
                aria-current={isActive ? 'step' : undefined}
                disabled={disabled}
                onClick={() => {
                  if (!isActive) onChange(makeStatusChangeEvent(name, opt.value))
                }}
              >
                <span className='invoice-status-stepper__dot' />
                <span className='invoice-status-stepper__label'>{opt.label}</span>
              </button>
            </React.Fragment>
          )
        })}
      </div>
    </div>
  )
}

export default StatusSelector
